import {ChangeDetectionStrategy, Component, input} from '@angular/core';

export type IconName =
  | 'arrow-up' | 'square' | 'x' | 'maximize' | 'minimize' | 'pen' | 'copy' | 'check' | 'thumbs-up' | 'thumbs-down'
  | 'rotate' | 'arrow-down' | 'file' | 'globe' | 'message' | 'search' | 'alert';

/**
 * Inline Lucide icons (https://lucide.dev, ISC license), copied as SVG paths so the widget has no
 * icon-font or runtime dependency. Always decorative: the button around it carries the label.
 */
@Component({
  selector: 'app-aa-icon',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {'class': 'aa-icon-host'},
  template: `
    <svg class="aa-icon" [class.aa-filled]="name() === 'square'" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      @switch (name()) {
        @case ('arrow-up') { <svg:path d="m5 12 7-7 7 7"/><svg:path d="M12 19V5"/> }
        @case ('square') { <svg:rect x="3" y="3" width="18" height="18" rx="3"/> }
        @case ('x') { <svg:path d="M18 6 6 18"/><svg:path d="m6 6 12 12"/> }
        @case ('maximize') {
          <svg:polyline points="15 3 21 3 21 9"/><svg:polyline points="9 21 3 21 3 15"/>
          <svg:line x1="21" x2="14" y1="3" y2="10"/><svg:line x1="3" x2="10" y1="21" y2="14"/>
        }
        @case ('minimize') {
          <svg:polyline points="4 14 10 14 10 20"/><svg:polyline points="20 10 14 10 14 4"/>
          <svg:line x1="14" x2="21" y1="10" y2="3"/><svg:line x1="3" x2="10" y1="21" y2="14"/>
        }
        @case ('pen') {
          <svg:path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
          <svg:path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"/>
        }
        @case ('copy') {
          <svg:rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
          <svg:path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
        }
        @case ('check') { <svg:path d="M20 6 9 17l-5-5"/> }
        @case ('thumbs-up') {
          <svg:path d="M7 10v12"/>
          <svg:path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"/>
        }
        @case ('thumbs-down') {
          <svg:path d="M17 14V2"/>
          <svg:path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z"/>
        }
        @case ('rotate') { <svg:path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><svg:path d="M3 3v5h5"/> }
        @case ('arrow-down') { <svg:path d="M12 5v14"/><svg:path d="m19 12-7 7-7-7"/> }
        @case ('file') {
          <svg:path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><svg:path d="M14 2v4a2 2 0 0 0 2 2h4"/>
          <svg:path d="M10 9H8"/><svg:path d="M16 13H8"/><svg:path d="M16 17H8"/>
        }
        @case ('globe') {
          <svg:circle cx="12" cy="12" r="10"/><svg:path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><svg:path d="M2 12h20"/>
        }
        @case ('message') { <svg:path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/> }
        @case ('search') { <svg:circle cx="11" cy="11" r="8"/><svg:path d="m21 21-4.3-4.3"/> }
        @case ('alert') {
          <svg:circle cx="12" cy="12" r="10"/><svg:line x1="12" x2="12" y1="8" y2="12"/><svg:line x1="12" x2="12.01" y1="16" y2="16"/>
        }
      }
    </svg>
  `,
})
export class IconComponent {
  readonly name = input.required<IconName>();
}
